import { Link } from "react-router-dom";
import { Heart, Compass, Users, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IMG } from "@/data/content";
import { Seo, breadcrumbLd } from "@/components/Seo";

const VALUES = [
  { icon: Heart, title: "People before products", desc: "Every chat starts with your life and what you're worried about, not a sales pitch." },
  { icon: Compass, title: "Plain talk, always", desc: "If it needs a jargon word, we'll explain it in words that actually make sense." },
  { icon: Users, title: "In it for the long haul", desc: "We stick around through the different stages of your life, not just the sign-up." },
];

export default function About() {
  return (
    <div>
      <Seo
        title="Our Story | Quantum Cover"
        description="Why Quantum Cover exists: to make insurance easier to understand and more personal for people across New Zealand, then connect you with a licensed adviser when you need one."
        path="/about"
        jsonLd={[
          { "@context": "https://schema.org", "@type": "Person", name: "Rahul Verma", jobTitle: "Founder", worksFor: { "@type": "Organization", name: "Quantum Cover" } },
          breadcrumbLd([{ name: "Home", path: "/" }, { name: "Our Story", path: "/about" }]),
        ]}
      />
      <section className="bg-[#0B3D2E] text-white pt-32 pb-20 overflow-hidden relative">
        <div className="absolute inset-0 qc-grid-bg opacity-30" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-400 mb-3">About us</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Our Story</h1>
            <p className="mt-5 text-lg text-slate-300 leading-relaxed">Insurance is one of those things most people know they should have, but many aren't sure where to begin. Between technical language, countless policy options and conflicting information online, it can quickly become overwhelming.</p>
          </div>
          <div>
            <div className="rounded-3xl ring-1 ring-white/10 shadow-2xl h-96 bg-white/5 grid place-items-center text-center px-6" data-testid="founder-headshot-placeholder">
              <div>
                <span className="grid place-items-center h-16 w-16 rounded-full bg-white/10 text-emerald-400 mx-auto"><UserRound size={30} /></span>
                <p className="mt-4 font-display font-medium text-white">Rahul Verma</p>
                <p className="text-sm text-emerald-300">Founder, Quantum Cover</p>
                <p className="mt-3 text-xs text-slate-400">Photo coming soon</p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <span className="h-10 w-1 rounded-full bg-emerald-400" />
              <div>
                <p className="font-display font-semibold text-white leading-tight">Rahul Verma</p>
                <p className="text-sm text-emerald-300">Founder, Quantum Cover</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-20">
        <div className="space-y-8 text-slate-700 leading-relaxed text-lg">
          <p className="font-display text-2xl sm:text-3xl font-semibold text-[#0B3D2E] leading-snug border-l-4 border-emerald-500 pl-5" data-testid="our-story-pullquote">That's why Quantum Cover exists.</p>
          <p>I wanted to create a place where insurance is explained clearly, people feel genuinely supported, and getting help is simple, whether that's in plain English or in the language you're most comfortable with. No pressure, no confusing jargon, just honest guidance to help you understand your options.</p>
          <p>When you're ready for advice that's tailored to your situation, we'll connect you with a licensed adviser who can provide advice that's appropriate for your individual circumstances.</p>
          <p>My hope is that Quantum Cover becomes a place people trust whenever they need help protecting what matters most.</p>
          <div className="pt-2">
            <p className="font-display font-semibold text-[#0B3D2E] text-base">Rahul Verma</p>
            <p className="text-slate-500 text-base">Founder, Quantum Cover</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20">
        <div className="grid md:grid-cols-3 gap-6">
          {VALUES.map((v) => (
            <div key={v.title} className="bg-white rounded-3xl border border-slate-200 p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
              <span className="grid place-items-center h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-600 mb-5"><v.icon size={24} /></span>
              <h3 className="font-display text-lg font-semibold text-[#0B3D2E]">{v.title}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 rounded-3xl bg-[#0B3D2E] px-6 py-10 sm:p-10 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white">Let's just have a chat</h2>
          <p className="mt-3 text-slate-300">No pressure and no obligation. Just a hand from someone who genuinely wants to help.</p>
          <Link to="/booking"><Button size="lg" className="mt-6 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-base">Book a Free Consultation</Button></Link>
        </div>
      </section>
    </div>
  );
}
